/**
 * Punto di ingresso della dashboard.
 *
 * Compone i quattro strati che l'applicazione richiede, in quest'ordine: il
 * provider di tema e locale di antd, il router del browser, il provider di
 * autenticazione (che possiede anche il client HTTP verso il gateway) e
 * l'applicazione vera.
 *
 * Il router sta **sopra** il provider di autenticazione perché le guardie di
 * rotta hanno bisogno sia dell'uno sia dell'altro, e `BrowserRouter` non
 * dipende da nulla.
 *
 * Qui non c'è alcuna configurazione di identità: non esistono variabili
 * `VITE_OIDC_*`, perché il browser non parla con Keycloak. L'unico parametro è
 * dove sta il gateway.
 *
 * Il tema invece non si aspetta: la preferenza si legge dal `localStorage` in
 * modo sincrono e si applica **prima** di creare la radice, con la stessa
 * regola che `public/theme-boot.js` ha già applicato nel `<head>` (in
 * sviluppo, nei test e in qualunque contesto senza quel file questa riga è
 * l'unica che lo fa).
 *
 * L'unica attesa prima del primo render è quella della lingua: se il
 * visitatore ha scelto (o il browser chiede) una lingua diversa dall'italiano,
 * le sue risorse arrivano da un chunk separato. Si aspetta quel chunk invece di
 * disegnare la pagina in italiano e correggerla un istante dopo — un lampo di
 * lingua sbagliata si nota, e su una pagina di accesso si legge come un
 * difetto.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { currentLanguage, ensureLanguage } from './i18n';
import { applyInitialTheme } from './ui/themeMode';
import { InstitorConfigProvider } from './ui/InstitorConfigProvider';
// Caratteri dentro il pacchetto (ADR 0021): Inter per il testo, Poppins per i titoli, solo
// l'alfabeto latino e i pesi usati.
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/poppins/latin-500.css';
import '@fontsource/poppins/latin-600.css';
import './styles.css';
import './workspace-styles.css';
import './shell-styles.css';
import './pages-styles.css';

// Prima di qualunque altra cosa: nessun `await` la precede, quindi il tema è
// deciso prima che esista una radice da disegnare.
applyInitialTheme();

/** URL base del gateway; stringa vuota significa «stessa origine». */
const gatewayUrl: string = import.meta.env.VITE_GATEWAY_URL ?? '';

const container = document.getElementById('root');
if (container === null) {
  throw new Error('elemento #root assente in index.html');
}

const root = createRoot(container);

/**
 * Monta l'applicazione quando la lingua iniziale è pronta.
 *
 * Un fallimento nel caricare la traduzione non deve lasciare la pagina bianca:
 * l'italiano è già in memoria e la dashboard parte comunque.
 *
 * @returns Una promessa che si risolve a montaggio avvenuto.
 */
async function start(): Promise<void> {
  try {
    await ensureLanguage(currentLanguage());
  } catch {
    // Chunk della lingua non raggiungibile: si prosegue con l'italiano, che è
    // sempre in bundle.
  }
  root.render(
    <StrictMode>
      <InstitorConfigProvider>
        <BrowserRouter>
          <AuthProvider baseUrl={gatewayUrl}>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </InstitorConfigProvider>
    </StrictMode>,
  );
}

void start();
